import {
  Badge,
  Box,
  Button,
  Grid,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { CopyIcon, ExternalLinkIcon } from '@chakra-ui/icons'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ChainBadge } from '@/components/common/ChainBadge'
import { StatusBadge } from '@/components/common/StatusBadge'
import type { SettlementRecord } from '@/types'
import { formatDateTime, getExplorerTxUrl, truncateHash } from '@/utils/formatUtils'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

interface SettlementRecordDetailModalProps {
  isOpen: boolean
  onClose: () => void
  selectedRecord: SettlementRecord | null
  onRefresh?: () => Promise<unknown> | void
  isRefreshing?: boolean
  subtitleColor: string
  queueConfigured: boolean
  manualAllowed: boolean
  isSubmitting: boolean
  onConfirmVerification: () => void
  getRecordString: (record: SettlementRecord, keys: readonly string[]) => string | undefined
  getRecordNumber: (record: SettlementRecord, keys: readonly string[]) => number | undefined
  getRecordBoolean: (record: SettlementRecord, keys: readonly string[]) => boolean | undefined
  getChainIdentifier: (record: SettlementRecord) => string
  isSolanaRecord: (record: SettlementRecord) => boolean
  formatAmountDisplay: (
    value: string | number | null | undefined,
    record: SettlementRecord
  ) => string
  formatGasUsed: (value?: string | number | null) => string
  formatGasPriceDisplay: (value?: string | number | null) => string
  handleCopy: (value: string, label?: string) => Promise<void>
}

const DetailRow = ({ label, value, subtitleColor }: {
  label: string
  value: ReactNode
  subtitleColor: string
}) => (
  <Grid
    templateColumns={{ base: 'repeat(1, minmax(0, 1fr))', md: '140px 1fr' }}
    columnGap={4}
    rowGap={1}
    py={1}
    width="100%"
    alignItems="start"
  >
    <Text fontSize="sm" fontWeight="medium" color={subtitleColor}>
      {label}
    </Text>
    <Box>
      {typeof value === 'string' || typeof value === 'number'
        ? <Text fontSize="sm">{value}</Text>
        : value}
    </Box>
  </Grid>
)

export default function SettlementRecordDetailModal({
  isOpen,
  onClose,
  selectedRecord,
  onRefresh,
  isRefreshing = false,
  subtitleColor,
  queueConfigured,
  manualAllowed,
  isSubmitting,
  onConfirmVerification,
  getRecordString,
  getRecordNumber,
  getRecordBoolean,
  getChainIdentifier,
  isSolanaRecord,
  formatAmountDisplay,
  formatGasUsed,
  formatGasPriceDisplay,
  handleCopy,
}: SettlementRecordDetailModalProps) {
  const { t } = useTranslation()

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW={{ base: '95vw', md: '1100px', xl: '1200px' }} maxH={{ base: '90vh', md: '80vh' }}>
        <ModalHeader pr={12}>
          <HStack justify="space-between" spacing={3}>
            <Text>{t('settlements.table.actions.modalTitle')}</Text>
            {onRefresh && (
              <RefreshIconButton
                label={t('common.refresh')}
                tooltipPlacement="left"
                onClick={() => void onRefresh()}
                isLoading={isRefreshing}
                isDisabled={isRefreshing}
                flexShrink={0}
              />
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          {selectedRecord && (
            <VStack align="stretch" spacing={3}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacingX={8} spacingY={3}>
                <Box gridColumn={{ base: 'auto', lg: '1 / -1' }}>
                  <DetailRow
                    label={t('settlements.table.columns.orderId')}
                    subtitleColor={subtitleColor}
                    value={(() => {
                      const primaryId = getRecordString(selectedRecord, ['orderId', 'order_id']) ?? '—'
                      const businessId = getRecordString(selectedRecord, [
                        'orderBusinessId',
                        'order_business_id',
                      ])
                      return (
                        <VStack align="flex-start" spacing={0}>
                          <Text fontFamily="mono" fontSize="sm">{primaryId}</Text>
                          {businessId && (
                            <Text fontSize="xs" color={subtitleColor}>{businessId}</Text>
                          )}
                        </VStack>
                      )
                    })()}
                  />
                </Box>

                <DetailRow
                  label={t('settlements.table.actions.settlementId')}
                  subtitleColor={subtitleColor}
                  value={<Text fontFamily="mono" fontSize="sm">{selectedRecord.id}</Text>}
                />

                <DetailRow
                  label={t('settlements.table.columns.transactionHash')}
                  subtitleColor={subtitleColor}
                  value={(() => {
                    const txHash = getRecordString(selectedRecord, ['transactionHash', 'transaction_hash'])
                    if (!txHash) {
                      return <Text fontSize="sm">{t('common.notAvailable')}</Text>
                    }
                    const explorerUrl = getExplorerTxUrl(getChainIdentifier(selectedRecord), txHash)
                    return (
                      <HStack spacing={2} align="center" flexWrap="wrap">
                        <Text fontFamily="mono" fontSize="sm">
                          {truncateHash(txHash, 10, 6)}
                        </Text>
                        <Tooltip label={t('common.copy')}>
                          <IconButton
                            aria-label={t('settlements.table.actions.copyHash')}
                            icon={<CopyIcon boxSize={3} />}
                            size="xs"
                            variant="ghost"
                            onClick={() => handleCopy(txHash, t('settlements.table.columns.transactionHash'))}
                          />
                        </Tooltip>
                        {explorerUrl && (
                          <Tooltip label={t('common.openExplorer')}>
                            <IconButton
                              aria-label={t('settlements.table.actions.openExplorer')}
                              icon={<ExternalLinkIcon boxSize={3} />}
                              size="xs"
                              variant="ghost"
                              onClick={() => {
                                if (typeof window !== 'undefined') {
                                  window.open(explorerUrl, '_blank', 'noopener,noreferrer')
                                }
                              }}
                            />
                          </Tooltip>
                        )}
                      </HStack>
                    )
                  })()}
                />

                <DetailRow
                  label={t('settlements.table.columns.chain')}
                  subtitleColor={subtitleColor}
                  value={<ChainBadge chainId={getChainIdentifier(selectedRecord)} />}
                />
                <DetailRow
                  label={t('settlements.table.columns.sequence')}
                  subtitleColor={subtitleColor}
                  value={
                    <Text fontSize="sm">
                      {getRecordNumber(selectedRecord, ['sweepSeq', 'sweep_seq']) ?? '—'}
                    </Text>
                  }
                />
                <DetailRow
                  label={t('settlements.table.columns.status')}
                  subtitleColor={subtitleColor}
                  value={(() => {
                    const statusId = getRecordString(selectedRecord, ['statusId', 'status_id']) ?? 'unknown'
                    return (
                      <StatusBadge
                        statusType="settlement"
                        status={statusId}
                        label={t(`settlements.table.statusLabel.${statusId}`, statusId)}
                      />
                    )
                  })()}
                />
                <DetailRow
                  label={t('settlements.table.columns.isVerified')}
                  subtitleColor={subtitleColor}
                  value={(() => {
                    const verified = getRecordBoolean(selectedRecord, ['isVerified', 'is_verified']) ?? false
                    return (
                      <Badge colorScheme={verified ? 'green' : 'gray'}>
                        {verified ? t('common.yes') : t('common.no')}
                      </Badge>
                    )
                  })()}
                />

                <DetailRow
                  label={t('settlements.table.columns.totalAmount')}
                  subtitleColor={subtitleColor}
                  value={
                    <Text fontSize="sm" fontFamily="mono">
                      {formatAmountDisplay(
                        getRecordString(selectedRecord, [
                          'totalAmountInSmallestUnit',
                          'total_amount_in_smallest_unit',
                        ]) ?? '0',
                        selectedRecord
                      )}
                    </Text>
                  }
                />
                <DetailRow
                  label={t('settlements.table.columns.merchantAmount')}
                  subtitleColor={subtitleColor}
                  value={
                    <Text fontSize="sm" fontFamily="mono">
                      {formatAmountDisplay(
                        getRecordString(selectedRecord, [
                          'merchantAmountInSmallestUnit',
                          'merchant_amount_in_smallest_unit',
                        ]) ?? '0',
                        selectedRecord
                      )}
                    </Text>
                  }
                />
                <DetailRow
                  label={t('settlements.table.columns.platformFee')}
                  subtitleColor={subtitleColor}
                  value={
                    <Text fontSize="sm" fontFamily="mono">
                      {formatAmountDisplay(
                        getRecordString(selectedRecord, [
                          'platformFeeInSmallestUnit',
                          'platform_fee_in_smallest_unit',
                        ]) ?? '0',
                        selectedRecord
                      )}
                    </Text>
                  }
                />

                <DetailRow
                  label={t('common.blockOrSlot', { defaultValue: 'Block / Slot' })}
                  subtitleColor={subtitleColor}
                  value={
                    <Text fontSize="sm">
                      {isSolanaRecord(selectedRecord)
                        ? (
                          getRecordString(selectedRecord, ['slot']) ??
                          getRecordString(selectedRecord, ['blockNumber', 'block_number']) ??
                          '—'
                        )
                        : (
                          getRecordString(selectedRecord, ['blockNumber', 'block_number']) ??
                          getRecordString(selectedRecord, ['slot']) ??
                          '—'
                        )}
                    </Text>
                  }
                />
                {!isSolanaRecord(selectedRecord) && (
                  <>
                    <DetailRow
                      label={t('settlements.table.actions.gasUsed')}
                      subtitleColor={subtitleColor}
                      value={
                        <Text fontSize="sm">
                          {formatGasUsed(getRecordString(selectedRecord, ['gasUsed', 'gas_used']))}
                        </Text>
                      }
                    />
                    <DetailRow
                      label={t('settlements.table.actions.gasPrice', 'Gas Price')}
                      subtitleColor={subtitleColor}
                      value={
                        <Text fontSize="sm">
                          {formatGasPriceDisplay(
                            getRecordString(selectedRecord, ['gasPrice', 'gas_price'])
                          )}
                        </Text>
                      }
                    />
                  </>
                )}

                <DetailRow
                  label={t('settlements.table.columns.createdAt')}
                  subtitleColor={subtitleColor}
                  value={(() => {
                    const createdAt = getRecordString(selectedRecord, ['createdAt', 'created_at'])
                    return createdAt ? formatDateTime(createdAt) : t('common.notAvailable')
                  })()}
                />
                <DetailRow
                  label={t('settlements.table.columns.updatedAt')}
                  subtitleColor={subtitleColor}
                  value={(() => {
                    const updatedAt = getRecordString(selectedRecord, ['updatedAt', 'updated_at'])
                    return updatedAt ? formatDateTime(updatedAt) : t('common.notAvailable')
                  })()}
                />
              </SimpleGrid>

              {getRecordString(selectedRecord, ['verificationNotes', 'verification_notes']) && (
                <Box>
                  <DetailRow
                    label={t('settlements.table.actions.verificationNotes')}
                    subtitleColor={subtitleColor}
                    value={
                      <Box maxH="120px" overflowY="auto">
                        <Text fontSize="sm" whiteSpace="pre-wrap">
                          {getRecordString(selectedRecord, ['verificationNotes', 'verification_notes'])!}
                        </Text>
                      </Box>
                    }
                  />
                </Box>
              )}
              {getRecordString(selectedRecord, ['settleNotes', 'settle_notes']) && (
                <Box>
                  <DetailRow
                    label={t('settlements.table.actions.settleNotes')}
                    subtitleColor={subtitleColor}
                    value={
                      <Box maxH="120px" overflowY="auto">
                        <Text fontSize="sm" whiteSpace="pre-wrap">
                          {getRecordString(selectedRecord, ['settleNotes', 'settle_notes'])!}
                        </Text>
                      </Box>
                    }
                  />
                </Box>
              )}

              <Text fontSize="sm" color={subtitleColor}>
                {t('settlements.table.actions.modalDescription')}
              </Text>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose}>
              {t('settlements.table.actions.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={onConfirmVerification}
              isLoading={isSubmitting}
              isDisabled={!queueConfigured || !manualAllowed}
            >
              {t('settlements.table.actions.confirm')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
